import React, { useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useMediaBlobUrl } from '../../hooks/useMediaBlobUrl';
import {
  buildGoogleGviewEmbedUrl,
  extractFilePreviewUrl,
  isImagePreviewContent,
  isImagePreviewUrl,
  shouldUseLocalBlobPreview,
} from '../../utils/filePreview';
import { normalizeMediaUrl } from '../../utils/normalizeMediaUrl';

type FileDocumentPreviewProps = {
  previewUrl?: string | null;
  conteudo?: string | null;
  title?: string;
  height?: string;
  showDownload?: boolean;
};

const FileDocumentPreview = ({
  previewUrl,
  conteudo,
  title = 'Documento',
  height = '75vh',
  showDownload = true,
}: FileDocumentPreviewProps): JSX.Element => {
  const [iframeLoading, setIframeLoading] = useState(true);

  const fileSourceUrl = useMemo(() => {
    return normalizeMediaUrl(previewUrl) || extractFilePreviewUrl(conteudo);
  }, [previewUrl, conteudo]);

  const isImage =
    isImagePreviewUrl(fileSourceUrl) || isImagePreviewContent(conteudo);
  const useBlobPreview =
    !isImage && shouldUseLocalBlobPreview(fileSourceUrl);
  const { blobUrl, loading: blobLoading, error: blobError } = useMediaBlobUrl(
    useBlobPreview ? fileSourceUrl : null
  );

  if (!fileSourceUrl) {
    return (
      <div className="py-4">
        <p>Não foi possível localizar o arquivo para visualização.</p>
      </div>
    );
  }

  if (useBlobPreview && blobLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-3 mb-0">Carregando documento...</p>
      </div>
    );
  }

  if (isImage) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: height,
        }}
      >
        <img
          src={fileSourceUrl}
          alt={title}
          style={{
            maxWidth: '100%',
            maxHeight: height,
            objectFit: 'contain',
            border: '1px solid #ccc',
          }}
        />
        {showDownload && (
          <a
            href={fileSourceUrl}
            download
            className="btn btn-primary mt-3"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download
          </a>
        )}
      </div>
    );
  }

  const iframeSrc = useBlobPreview
    ? blobUrl
    : buildGoogleGviewEmbedUrl(fileSourceUrl);

  if (!iframeSrc || (useBlobPreview && blobError)) {
    return (
      <div className="py-4 text-center">
        <p>Não foi possível exibir o documento nesta tela.</p>
        <p className="mb-0">
          <a href={fileSourceUrl} target="_blank" rel="noopener noreferrer">
            Abrir arquivo em nova aba
          </a>
        </p>
        {showDownload && (
          <a
            href={fileSourceUrl}
            download
            className="btn btn-primary mt-3"
          >
            Download
          </a>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: height,
      }}
    >
      {iframeLoading && <Spinner animation="border" className="my-3" />}
      <iframe
        src={iframeSrc}
        title={title}
        style={{
          width: '100%',
          height,
          border: 'none',
          display: iframeLoading ? 'none' : 'block',
        }}
        onLoad={() => setIframeLoading(false)}
        onError={() => setIframeLoading(false)}
      />
      {!iframeLoading && (
        <p className="text-center mt-2 mb-0">
          Se o documento não carregou, use o botão de download abaixo.
        </p>
      )}
      {showDownload && (
        <a
          href={fileSourceUrl}
          download
          className="btn btn-primary mt-3"
          target="_blank"
          rel="noopener noreferrer"
        >
          Download
        </a>
      )}
    </div>
  );
};

export default FileDocumentPreview;
